/* Query and mutation hooks for the guest list — STORY-016.
 *
 * Every write answers with the whole list and both totals, so each mutation
 * writes the response straight into the cache rather than invalidating and
 * refetching. That is not an optimisation: the totals move whenever any one
 * Attendance does, and refetching would leave a visible moment where the
 * headcount disagrees with the rows above it.
 */

import {
  type AddAttendanceRequest,
  guestListResponseSchema,
  type ListGuestsQuery,
  type UpdateAttendanceRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

/**
 * The key one page of a guest list is cached under.
 *
 * The page is part of it, so two pages are two cached results rather than one
 * that flickers between them. Every write answers with the page it was made
 * from and is written straight back, so nothing here refetches on an edit.
 */
export const guestListKey = (organisationId: string, eventId: string, query?: ListGuestsQuery) =>
  query === undefined
    ? (['organisations', organisationId, 'events', eventId, 'guests'] as const)
    : (['organisations', organisationId, 'events', eventId, 'guests', query] as const);

const path = (organisationId: string, eventId: string) =>
  `/organisations/${organisationId}/events/${eventId}/guests`;

/**
 * The page, on a write as well as on a read.
 *
 * Every write answers with a page of the list, and the response is put straight
 * into the cache entry for the page it was made from. Without these parameters
 * the server answered with the FIRST page and the client filed it under page
 * three — so marking somebody paid replaced the visible rows with guests 1–10
 * while the pager still read "page 3 of 4".
 */
const toSearchParams = (query: ListGuestsQuery): string => {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
};

export function useGuestList(organisationId: string, eventId: string, query: ListGuestsQuery = {}) {
  return useQuery({
    queryKey: guestListKey(organisationId, eventId, query),
    queryFn: () =>
      request(`${path(organisationId, eventId)}${toSearchParams(query)}`, {
        schema: guestListResponseSchema,
      }),
    /* The previous page stays while the next loads, so paging does not blank
     * the list and bounce the scroll position. */
    placeholderData: (previous) => previous,
    retry: false,
  });
}

export function useAddGuest(
  organisationId: string,
  eventId: string,
  currentQuery: ListGuestsQuery = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: AddAttendanceRequest) =>
      request(`${path(organisationId, eventId)}${toSearchParams(currentQuery)}`, {
        method: 'POST',
        schema: guestListResponseSchema,
        body: values,
      }),
    onSuccess: async (list, _variables, _context) => {
      /* The page this write was made from is written back, so the totals and
       * the rows it returned are on screen immediately — and every OTHER page
       * is invalidated, because a removal shifts every row after it onto a
       * different page. Writing one page and trusting the rest is how a guest
       * appears twice. */
      queryClient.setQueryData(guestListKey(organisationId, eventId, currentQuery), list);
      await queryClient.invalidateQueries({
        queryKey: guestListKey(organisationId, eventId),
        /* Not the page just written: it holds the server's own answer, and
         * refetching it would replace a correct result with an identical one. */
        predicate: (cached) =>
          JSON.stringify(cached.queryKey) !==
          JSON.stringify(guestListKey(organisationId, eventId, currentQuery)),
      });
    },
  });
}

export function useUpdateGuest(
  organisationId: string,
  eventId: string,
  currentQuery: ListGuestsQuery = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ attendanceId, ...values }: UpdateAttendanceRequest & { attendanceId: string }) =>
      request(`${path(organisationId, eventId)}/${attendanceId}${toSearchParams(currentQuery)}`, {
        method: 'PATCH',
        schema: guestListResponseSchema,
        body: values,
      }),
    onSuccess: async (list, _variables, _context) => {
      /* The page this write was made from is written back, so the totals and
       * the rows it returned are on screen immediately — and every OTHER page
       * is invalidated, because a removal shifts every row after it onto a
       * different page. Writing one page and trusting the rest is how a guest
       * appears twice. */
      queryClient.setQueryData(guestListKey(organisationId, eventId, currentQuery), list);
      await queryClient.invalidateQueries({
        queryKey: guestListKey(organisationId, eventId),
        /* Not the page just written: it holds the server's own answer, and
         * refetching it would replace a correct result with an identical one. */
        predicate: (cached) =>
          JSON.stringify(cached.queryKey) !==
          JSON.stringify(guestListKey(organisationId, eventId, currentQuery)),
      });
    },
  });
}

export function useRemoveGuest(
  organisationId: string,
  eventId: string,
  currentQuery: ListGuestsQuery = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attendanceId: string) =>
      request(`${path(organisationId, eventId)}/${attendanceId}${toSearchParams(currentQuery)}`, {
        method: 'DELETE',
        schema: guestListResponseSchema,
      }),
    onSuccess: async (list, _variables, _context) => {
      /* The page this write was made from is written back, so the totals and
       * the rows it returned are on screen immediately — and every OTHER page
       * is invalidated, because a removal shifts every row after it onto a
       * different page. Writing one page and trusting the rest is how a guest
       * appears twice. */
      queryClient.setQueryData(guestListKey(organisationId, eventId, currentQuery), list);
      await queryClient.invalidateQueries({
        queryKey: guestListKey(organisationId, eventId),
        /* Not the page just written: it holds the server's own answer, and
         * refetching it would replace a correct result with an identical one. */
        predicate: (cached) =>
          JSON.stringify(cached.queryKey) !==
          JSON.stringify(guestListKey(organisationId, eventId, currentQuery)),
      });
    },
  });
}
